import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import React, { useEffect, useState, type JSX } from 'react';
import { EuroFormat } from '../common/inputFormats';
import { dialogActions } from './Dialog';
import InputAdornment from '@mui/material/InputAdornment';
import Grid from '@mui/material/Grid';
import { SPACING_BETWEEN_FORM_FIELDS } from '../common/spacings';
import { DateiKategorie, Unterzeichner } from '../../shared/constants';

type Props = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onEdit: (kategorie: string, wert?: number | null, unterzeichner?: Unterzeichner) => void;
  readonly kategorien: string[];
  readonly text: string;
  readonly titel: string;
  readonly saveButtonText: string;
};

const UNTERZEICHNER_OPTIONEN = [
  { value: Unterzeichner.AUFTRAGGEBER, label: 'Auftraggeber' },
  { value: Unterzeichner.VERMITTLER, label: 'Vermittler' },
  { value: Unterzeichner.FAHRZEUGHALTER, label: 'Fahrzeughalter' }
];

export function KategorieEditierenDialog({ open, onClose, onEdit, kategorien, text, titel, saveButtonText }: Props): JSX.Element {
  const [kategorie, setKategorie] = useState(kategorien[0]);
  const [wert, setWert] = useState(0);
  const [unterzeichnerAuswahl, setUnterzeichnerAuswahl] = useState<Unterzeichner | ''>('');

  useEffect(() => {
    setWert(0);
    setUnterzeichnerAuswahl('');
  }, [kategorie]);

  useEffect(() => {
    if (kategorien.length > 0) {
      setKategorie(kategorien[0]);
    }
  }, [kategorien]);

  const zeigeWertEintrag = [DateiKategorie.FREMDRECHNUNG].includes(kategorie as DateiKategorie);
  const zeigeUnterzeichnerAuswahl = kategorie === DateiKategorie.UNTERSCHRIFTEN;
  const istWertBeiFremdrechnungVorhanden = (zeigeWertEintrag && wert > 0) || !zeigeWertEintrag;
  const istUnterzeichnerBeiUnterschriftenVorhanden = !zeigeUnterzeichnerAuswahl || Boolean(unterzeichnerAuswahl);
  const isSaveDisabled = !istWertBeiFremdrechnungVorhanden || !istUnterzeichnerBeiUnterschriftenVorhanden;

  const handleSave = () => {
    onEdit(kategorie, wert > 0 ? wert : null, unterzeichnerAuswahl || undefined);
  };

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="kategorieHinzufuegen">
      <DialogTitle id="kategorieHinzufuegen">{titel}</DialogTitle>
      <DialogContent>
        <DialogContentText>{text}</DialogContentText>
        <Grid container spacing={SPACING_BETWEEN_FORM_FIELDS}>
          <Grid size={12}>
            <FormControl variant="standard" fullWidth>
              <InputLabel>Kategorie</InputLabel>
              <Select
                variant="standard"
                label="Kategorie"
                value={kategorie ?? kategorien[0]}
                onChange={(event) => {
                  setKategorie(event.target.value);
                }}
                data-testid="dokument-kategorie-auswahl"
              >
                {kategorien.map((k) => (
                  <MenuItem key={k} value={k} data-testid={k}>
                    {k}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {zeigeWertEintrag && (
            <Grid size={12}>
              <TextField
                variant="standard"
                label="Nettowert"
                value={wert}
                onChange={(event) => setWert(Number(event.target.value))}
                fullWidth
                data-testid="fremdrechnung-nettowert"
                InputProps={{
                  inputComponent: EuroFormat,
                  endAdornment: <InputAdornment position="end">€</InputAdornment>
                }}
              />
            </Grid>
          )}
          {zeigeUnterzeichnerAuswahl && (
            <Grid size={12}>
              <FormControl variant="standard" fullWidth required>
                <InputLabel>Unterzeichner</InputLabel>
                <Select
                  variant="standard"
                  label="Unterzeichner"
                  value={unterzeichnerAuswahl}
                  onChange={(event) => setUnterzeichnerAuswahl(event.target.value)}
                  data-testid="unterschriften-unterzeichner"
                >
                  {UNTERZEICHNER_OPTIONEN.map((option) => (
                    <MenuItem key={option.value} value={option.value} data-testid={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={dialogActions}>
        <Button autoFocus onClick={onClose} variant="contained" color="secondary">
          Abbrechen
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={isSaveDisabled}>
          {saveButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
